import React, { Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./index.scss";
import "bootstrap-icons/font/bootstrap-icons.css";
import "react-datepicker/dist/react-datepicker.css";
import "./styles/datepicker-dark.css";

import { LoadingProvider } from "./context/LoadingContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AxiosInitializer from "./config/AxiosInitializer";
import Loader from "./components/Common/Loader";
import ProtectedRoute from "./routes/ProtectedRoute";
import LoaderStatic from "./components/Common/LoaderStatic";
import { LockscreenProvider } from "./context/LockscreenContext";
import LockscreenModal from "./components/Common/LockscreenModal";
import Unauthorized from "./components/Common/Unauthorized";

// Módulos existentes
import CompanyPage from "./components/Company";
import CompanyForm from "./components/Company/Form";
import CompanyAdminUserPage from "./components/CompanyAdminUser";
import CompanyAdminUserForm from "./components/CompanyAdminUser/Form";
import CompanyLicensePage from "./components/CompanyLicense";
import CompanyLicenseForm from "./components/CompanyLicense/Form";
import SecurityPermissionPage from "./components/SecurityPermission";
import SecurityPermissionForm from "./components/SecurityPermission/Form";
import SecurityRolePage from "./components/SecurityRoles";
import SecurityRoleForm from "./components/SecurityRoles/Form";
import LawyerPage from "./components/Lawyer";
import LawyerForm from "./components/Lawyer/Form";
import LawyerDeletedPage from "./components/Lawyer/deleted";
import CustomerPage from "./components/Customer";
import CustomerForm from "./components/Customer/Form";
import CustomerDeletedPage from "./components/Customer/deleted";
import CasesPage from "./components/Case";
import CaseForm from "./components/Case/Form";
import CaseTimelinePage from "./components/Case/CaseTimelinePage";
import CaseManagePage from "./components/Case/CaseManagePage";
import NovianCoreLandingPage from "./components/Landing/NovianCoreLandingPage";
import LawyerWorkloadDashboard from "./components/Lawyer/LawyerWorkloadDashboard";

// Autenticación
const Login = lazy(() => import("./components/Authentication/Login/Login"));
const ForgotPassword = lazy(() =>
  import("./components/Authentication/PasswordRecovery/ForgotPassword")
);
const ResetPassword = lazy(() =>
  import("./components/Authentication/PasswordRecovery/ResetPassword")
);

// App
const App = lazy(() => import("./components/app"));
const Dashboard = lazy(() => import("./components/Dashboard/Dashboard"));
const Profile = lazy(() => import("./components/Profile/Profile"));

const RootApp = () => {
  const { isAuthChecking } = useAuth();

  if (isAuthChecking) return <Loader />;

  return (
    <BrowserRouter>
      <Suspense fallback={<LoaderStatic />}>
        <Routes>
          {/* Públicas */}
          <Route path="/" element={<NovianCoreLandingPage />} />

          {/* Autenticación */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Protegidas */}
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute>
                <App />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="profile" element={<Profile />} />
            {/* Empresas */}
            <Route
              path="company"
              element={
                <ProtectedRoute requiredPermission="companies.view">
                  <CompanyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="company/create"
              element={
                <ProtectedRoute requiredPermission="companies.create">
                  <CompanyForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="company/edit/:id"
              element={
                <ProtectedRoute requiredPermission="companies.update">
                  <CompanyForm />
                </ProtectedRoute>
              }
            />
            {/* Usuarios administradores de empresa */}
            <Route
              path="company-admin-user"
              element={
                <ProtectedRoute requiredPermission="company_admins.view">
                  <CompanyAdminUserPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="company-admin-user/create"
              element={
                <ProtectedRoute requiredPermission="company_admins.create">
                  <CompanyAdminUserForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="company-admin-user/edit/:id"
              element={
                <ProtectedRoute requiredPermission="company_admins.update">
                  <CompanyAdminUserForm />
                </ProtectedRoute>
              }
            />
            {/* Licencias */}
            <Route
              path="company-license"
              element={
                <ProtectedRoute requiredPermission="licenses.view">
                  <CompanyLicensePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="company-license/create"
              element={
                <ProtectedRoute requiredPermission="licenses.create">
                  <CompanyLicenseForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="company-license/edit/:id"
              element={
                <ProtectedRoute requiredPermission="licenses.update">
                  <CompanyLicenseForm />
                </ProtectedRoute>
              }
            />
            {/* Nuevas rutas para permisos de seguridad */}
            <Route
              path="security-permission"
              element={
                <ProtectedRoute requiredPermission="security_permissions.view">
                  <SecurityPermissionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="security-permission/create"
              element={
                <ProtectedRoute requiredPermission="security_permissions.create">
                  <SecurityPermissionForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="security-permission/edit/:id"
              element={
                <ProtectedRoute requiredPermission="security_permissions.update">
                  <SecurityPermissionForm />
                </ProtectedRoute>
              }
            />
            {/* Nuevas rutas para roles de seguridad */}
            <Route
              path="security-role"
              element={
                <ProtectedRoute requiredPermission="security_roles.view">
                  <SecurityRolePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="security-role/create"
              element={
                <ProtectedRoute requiredPermission="security_roles.create">
                  <SecurityRoleForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="security-role/edit/:id"
              element={
                <ProtectedRoute requiredPermission="security_roles.update">
                  <SecurityRoleForm />
                </ProtectedRoute>
              }
            />
            {/* Lawyer Routes */}
            <Route
              path="lawyers"
              element={
                <ProtectedRoute requiredPermission="abogados.view">
                  <LawyerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="lawyers/create"
              element={
                <ProtectedRoute requiredPermission="abogados.create">
                  <LawyerForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="lawyers/edit/:id"
              element={
                <ProtectedRoute requiredPermission="abogados.update">
                  <LawyerForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="lawyers/deleted"
              element={
                <ProtectedRoute requiredPermission="abogados.view_deleted">
                  <LawyerDeletedPage />
                </ProtectedRoute>
              }
            />
            {/* Customer Routes */}
            <Route
              path="customers"
              element={
                <ProtectedRoute requiredPermission="clientes.view">
                  <CustomerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="customers/create"
              element={
                <ProtectedRoute requiredPermission="clientes.create">
                  <CustomerForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="customers/edit/:id"
              element={
                <ProtectedRoute requiredPermission="clientes.update">
                  <CustomerForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="customers/deleted"
              element={
                <ProtectedRoute requiredPermission="clientes.view_deleted">
                  <CustomerDeletedPage />
                </ProtectedRoute>
              }
            />

            {/* Case Routes */}

            <Route
              path="cases/create"
              element={
                <ProtectedRoute requiredPermission="casos.create">
                  <CaseForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="cases/edit/:id"
              element={
                <ProtectedRoute requiredPermission="casos.update">
                  <CaseForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="cases/:id/timeline"
              element={
                <ProtectedRoute requiredPermission="casos.view.timeline">
                  <CaseTimelinePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="cases/:id/manage"
              element={
                <ProtectedRoute requiredPermission="casos.gestion">
                  <CaseManagePage />
                </ProtectedRoute>
              }
            />
            {/* TODOS LOS CASOS (solo admins) */}
            <Route
              path="cases"
              element={
                <ProtectedRoute requiredPermission="casos.view.all">
                  <CasesPage />
                </ProtectedRoute>
              }
            />

            {/* MIS CASOS (para abogados) */}
            <Route
              path="my-cases"
              element={
                <ProtectedRoute requiredPermission="casos.view">
                  <CasesPage />
                </ProtectedRoute>
              }
            />
            {/* Lawyer Workload Dashboard */}
            <Route
              path="lawyers/workload-dashboard"
              element={
                <ProtectedRoute requiredPermission="casos_dashboard.view.all">
                  <LawyerWorkloadDashboard />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Página de no autorizado */}
          <Route path="/unauthorized" element={<Unauthorized />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

const root = createRoot(document.getElementById("root"));

root.render(
  <React.StrictMode>
    <LoadingProvider>
      <LockscreenProvider>
        <AuthProvider>
          <AxiosInitializer />
          <Loader />
          <LockscreenModal />
          <RootApp />
        </AuthProvider>
      </LockscreenProvider>
    </LoadingProvider>
  </React.StrictMode>
);
